<template>
  <v-dialog :model-value="open" @update:model-value="val => $emit('close')" max-width="640">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>延长音频</span>
        <span v-if="card" class="text-subtitle-1 text-truncate ml-4">{{ card.title }}</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-snackbar :timeout="2000" v-model="snackbar" color="white">
          <div class="text-subtitle-1 pb-2">提示</div>
          <p>{{ text2 }}</p>
          <template v-slot:actions>
            <v-btn color="pink" variant="text" @click="snackbar = false">关闭</v-btn>
          </template>
        </v-snackbar>

        <div class="mb-3">
          <div class="mb-1">模型选择</div>
          <v-select v-model="selectedModel" :items="modelOptions" label="选择续写模型" variant="outlined" density="compact"></v-select>
          <div class="text-caption text-medium-emphasis">建议选择与源歌曲相同的模型，保证续写风格连贯。</div>
        </div>

        <template v-if="isV3Model(selectedModel)">
          <div class="mb-3">
            <div class="d-flex justify-space-between">
              <span>源曲上下文秒数</span><span>{{ contextSeconds }}</span>
            </div>
            <v-slider v-model="contextSeconds" :min="1" :max="284" step="1" hide-details></v-slider>
            <div class="text-caption text-medium-emphasis">越大与源曲越连贯、新增内容越短。</div>
          </div>
          <div class="mb-3">
            <div class="d-flex justify-space-between">
              <span>裁剪源曲秒数</span><span>{{ cropDuration }}</span>
            </div>
            <v-slider v-model="cropDuration" :min="0" :max="60" step="1" hide-details></v-slider>
            <div class="text-caption text-medium-emphasis">先裁剪源曲结尾多少秒再续写，用于切掉尾奏。</div>
          </div>
          <v-expansion-panels variant="accordion" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title>高级参数</v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="mb-3">
                  <div class="mb-1">目标总时长（秒）</div>
                  <div class="d-flex align-center">
                    <v-select v-model="lengthRangeMin" :items="lengthRangeOptions" label="最短" variant="outlined" density="compact" class="mr-2"></v-select>
                    <span class="mx-1">~</span>
                    <v-select v-model="lengthRangeMax" :items="lengthRangeOptions" label="最长" variant="outlined" density="compact"></v-select>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="mb-1">避免风格（negative_tags）</div>
                  <v-text-field v-model="negativeTags" placeholder="多个标签用逗号隔开，如 sad, metal" variant="outlined" density="compact"></v-text-field>
                </div>
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span>提示词遵从度</span><span>{{ promptStrength }}</span>
                  </div>
                  <v-slider v-model="promptStrength" :min="1.0" :max="4.0" step="0.1" @update:modelValue="onPromptStrengthChange"></v-slider>
                </div>
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span>风格遵从度</span><span>{{ styleScale }}</span>
                  </div>
                  <v-slider v-model="styleScale" :min="1.0" :max="12.0" step="0.1" @update:modelValue="onStyleScaleChange"></v-slider>
                </div>
                <div class="text-caption text-medium-emphasis mt-3">
                  目标时长为30秒的倍数：最短0~270秒，最长30~300秒。提示词遵从度（1.0~4.0）与风格遵从度（1.0~12.0）恰有一个可大于1.0。
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template v-else>
          <div class="mb-3">
            <div class="mb-1">续写方向</div>
            <v-radio-group v-model="side" inline hide-details>
              <v-radio label="向左续写" value="left"></v-radio>
              <v-radio label="向右续写" value="right"></v-radio>
            </v-radio-group>
          </div>
          <div class="mb-3">
            <div class="d-flex justify-space-between">
              <span>新增时长（秒）</span><span>{{ extendDuration }}</span>
            </div>
            <v-slider v-model="extendDuration" :min="1" :max="85" step="1" hide-details></v-slider>
            <div class="text-caption text-medium-emphasis">越大新增内容越多。</div>
          </div>
          <div class="mb-3">
            <div class="d-flex justify-space-between">
              <span>裁剪源曲秒数</span><span>{{ cropDuration }}</span>
            </div>
            <v-slider v-model="cropDuration" :min="0" :max="60" step="1" hide-details></v-slider>
            <div class="text-caption text-medium-emphasis">先裁剪源曲开头/结尾多少秒再续写。</div>
          </div>
          <v-expansion-panels variant="accordion" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title>高级参数</v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span>BPM</span><span>{{ bpmDisplay(bpmIndex) }}</span>
                  </div>
                  <div class="d-flex align-center">
                    <span class="text-caption mr-2">auto</span>
                    <v-slider v-model="bpmIndex" :min="0" :max="131" step="1" hide-details></v-slider>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="mb-1">随机种子</div>
                  <v-text-field v-model="seed" placeholder="相同种子+标签/歌词可复刻同一首歌" variant="outlined" density="compact"></v-text-field>
                </div>
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span>人声/伴奏平衡</span><span>{{ balanceStrength }}</span>
                  </div>
                  <v-slider v-model="balanceStrength" :min="0.1" :max="1.0" step="0.1"></v-slider>
                </div>
                <div class="text-caption text-medium-emphasis mt-3">
                  BPM 滑块最左侧为 auto，由 AI 自动挑选；往右拖动为固定节拍数。人声/伴奏平衡官方推荐 0.7。
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <div class="mb-3 mt-2">
          <div class="mb-1">续写引导（可选）</div>
          <v-text-field v-model="prompt" placeholder="如：Write another verse for my song" variant="outlined" density="compact"></v-text-field>
        </div>
        <div class="mb-3">
          <div class="mb-1">风格标签（可选）</div>
          <v-text-field v-model="style" placeholder="多个风格用逗号隔开，为空时按源曲风格续写" variant="outlined" density="compact"></v-text-field>
        </div>
        <div class="mb-3">
          <div class="mb-1">歌名（可选）</div>
          <v-text-field v-model="title" placeholder="可为空" variant="outlined" density="compact"></v-text-field>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text="取消" class="text-green" variant="text" @click="$emit('close')"></v-btn>
        <v-btn :loading="loading" :disabled="loading" color="primary" @click="submit">
          {{ loading ? loadingText : '开始续写' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ExtendDialog',
  props: {
    open: { type: Boolean, default: false },
    card: { type: Object, default: null }
  },
  emits: ['close', 'success'],
  data: () => ({
    modelOptions: [],
    modelMap: {},
    selectedModel: 'chirp-v3-5',
    side: 'right',
    extendDuration: 30,
    contextSeconds: 30,
    cropDuration: 0,
    prompt: '',
    style: '',
    title: '',
    lengthRangeMin: 90,
    lengthRangeMax: 180,
    negativeTags: '',
    promptStrength: 1.0,
    styleScale: 4.5,
    bpmIndex: 0,
    seed: '',
    balanceStrength: 0.7,
    lengthRangeOptions: [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300],
    loading: false,
    loadingText: '',
    loadingTextIndex: 0,
    loadingTimer: null,
    loadingTexts: ['正在创建续写任务...', '正在提交请求...', 'AI 正在推理...', '正在生成音乐...', '即将完成...'],
    snackbar: false,
    text2: ''
  }),
  watch: {
    open(val) {
      if (val) {
        this.reset();
        this.getModelList();
      }
    }
  },
  methods: {
    reset() {
      this.side = 'right';
      this.extendDuration = 30;
      this.contextSeconds = 30;
      this.cropDuration = 0;
      this.prompt = '';
      this.style = '';
      this.title = '';
      this.lengthRangeMin = 90;
      this.lengthRangeMax = 180;
      this.negativeTags = '';
      this.promptStrength = 1.0;
      this.styleScale = 4.5;
      this.bpmIndex = 0;
      this.seed = '';
      this.balanceStrength = 0.7;
      this.loading = false;
      this.stopLoadingText();
    },
    getModelList() {
      this.$axios.get('/music/api/models').then(response => {
        if (response.data.type === 'ok' && response.data.code === 2000) {
          const enabledModels = response.data.data.filter(model => model.isEnabled);
          this.modelOptions = enabledModels.map(model => model.name);
          this.modelMap = enabledModels.reduce((map, model) => {
            map[model.name] = model.integralCost;
            return map;
          }, {});
          if (enabledModels.length > 0) {
            this.selectedModel = enabledModels[0].name;
          }
        }
      }).catch(error => {
      });
    },
    isV3Model(model) {
      const m = (model || '').toLowerCase();
      return m.startsWith('chirp-v4') || m.startsWith('chirp-v5');
    },
    bpmDisplay(index) {
      if (index === 0) {
        return 'auto';
      }
      return 70 + (index - 1);
    },
    onPromptStrengthChange() {
      if (this.promptStrength > 1.0) {
        this.styleScale = 1.0;
      }
    },
    onStyleScaleChange() {
      if (this.styleScale > 1.0) {
        this.promptStrength = 1.0;
      }
    },
    startLoadingText() {
      this.loadingTextIndex = 0;
      this.loadingText = this.loadingTexts[0];
      this.loadingTimer = setInterval(() => {
        this.loadingTextIndex = (this.loadingTextIndex + 1) % this.loadingTexts.length;
        this.loadingText = this.loadingTexts[this.loadingTextIndex];
      }, 1500);
    },
    stopLoadingText() {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.loadingText = '';
    },
    submit() {
      if (!this.card || !this.card.audio) {
        this.snackbar = true;
        this.text2 = "缺少源音频地址，无法续写";
        return;
      }
      if (this.prompt && this.prompt.length > 120) {
        this.snackbar = true;
        this.text2 = "提示词不能超过120字";
        return;
      }
      const json = {
        audio_url: this.card.audio,
        model: this.selectedModel,
        prompt: this.prompt,
        style: this.style,
        title: this.title,
        crop_duration: this.cropDuration
      };
      if (this.isV3Model(this.selectedModel)) {
        if (this.lengthRangeMin > this.lengthRangeMax) {
          this.snackbar = true;
          this.text2 = "目标时长范围设置错误：最短时长不能大于最长时长";
          return;
        }
        json.context_seconds = this.contextSeconds;
        json.length_range = [this.lengthRangeMin, this.lengthRangeMax];
        const negativeTags = this.negativeTags.split(',').map(s => s.trim()).filter(s => s !== '');
        if (negativeTags.length > 0) {
          json.negative_tags = negativeTags;
        }
        json.prompt_strength = this.promptStrength;
        json.style_scale = this.styleScale;
      } else {
        json.side = this.side;
        json.extend_duration = this.extendDuration;
        if (this.bpmIndex === 0) {
          json.bpm = 'auto';
        } else {
          json.bpm = 70 + (this.bpmIndex - 1);
        }
        if (this.seed && this.seed !== '') {
          const seedNum = parseInt(this.seed, 10);
          if (!isNaN(seedNum)) {
            json.seed = seedNum;
          }
        }
        json.balance_strength = this.balanceStrength;
      }

      this.loading = true;
      this.startLoadingText();
      this.$axios.post('/music/api/extend/ace', json)
        .then(response => {
          this.loading = false;
          this.stopLoadingText();
          if (response.status === 200 && response.data.type === 'ok' && response.data.code === 2000) {
            this.$emit('success');
            this.$emit('close');
          } else {
            this.snackbar = true;
            this.text2 = '请求失败:' + (response.data.message || '');
          }
        })
        .catch(error => {
          this.loading = false;
          this.stopLoadingText();
          if (error.response && error.response.data && error.response.data.message) {
            this.snackbar = true;
            this.text2 = error.response.data.message;
          } else {
            this.snackbar = true;
            this.text2 = '网络连接失败，请稍后重试';
          }
        });
    }
  }
};
</script>